/**
 * @module utils/qbLinkage/runPipeline
 * @description 按 Phase 顺序执行 handlers，合并为单次 patch
 */

import type {
  IQBChangeSet,
  IQBLinkageContext,
  IQBLinkageHandler,
  IQBLinkagePatch,
  TQBLinkagePhase,
} from './types';
import { filterHandlersForChangeSet } from './shouldRun';

/**
 * QB 联动 handler 执行顺序（不可打乱）：
 * - prune：QB 删维/删指后，移除配置里指向已删除字段的条目或 key
 * - migrate：字段 column_name 身份变更时，将配置中的引用 key 迁移到新 key
 * - sync：按当前 QB 结构增量同步配置（如 crosstab 行列、column_sort 顺序）
 * - sanitize：在前三阶段之后，裁剪因维/指数量或图表类型而不再合法的选项值
 */
const PHASE_ORDER: TQBLinkagePhase[] = [
  'prune',
  'migrate',
  'sync',
  'sanitize',
];

export function runQBLinkagePipeline(
  changeSet: IQBChangeSet,
  ctx: IQBLinkageContext,
  handlers: IQBLinkageHandler[],
): IQBLinkagePatch {
  const basePatch: Record<string, unknown> = {};
  const draftPatch: Record<string, unknown> = {};

  const activeHandlers = filterHandlersForChangeSet(handlers, changeSet);
  const byPhase = PHASE_ORDER.map(phase =>
    activeHandlers.filter(h => h.phase === phase),
  );

  /**
   * 同一次 pipeline 内后续 handler 应读到前序 handler 已写入的 base/draft。
   * 例如：sync 阶段 columnSortReorderHandler 更新 column_sort 后，
   * sanitize 阶段 columnSortSanitizeHandler 在 reconcile 时使用最新 draft。
   */
  const workingCtx: IQBLinkageContext = {
    ...ctx,
    base: { ...ctx.base },
    draft: { ...ctx.draft },
  };

  for (const phaseHandlers of byPhase) {
    for (const handler of phaseHandlers) {
      const out = handler.apply(changeSet, workingCtx);
      if (!out) {
        continue;
      }
      if (out.base) {
        Object.assign(basePatch, out.base);
        Object.assign(workingCtx.base, out.base);
      }
      if (out.draft) {
        Object.assign(draftPatch, out.draft);
        Object.assign(workingCtx.draft, out.draft);
      }
    }
  }

  return { base: basePatch, draft: draftPatch };
}
